import fontMap from '@/assets/font.json'
import { buildCurrentProjectResourceUrl } from '@/features/static-resource/resourceCdn'
import { appendMaterialResourceMappingEntry } from '@/features/material-library/resourceMapping'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import type DataFileMap from '@/model/Resource/DataFileMap'

export const FONT_LIBRARY_KEY_LINK_INDEX = 1
export const FONT_DISPLAY_NAME_LINK_INDEX = 2
export const FONT_PREVIEW_MD5_LINK_INDEX = 8
export const FONT_LIBRARY_DEFAULT_FOLDER = '文件'

export interface OfficialFontInfo {
  name: string
  displayName: string
  path: string
  md5: string
  size: number
  previewMd5: string
}

export interface OfficialFontItem extends OfficialFontInfo {
  key: string
  categoryPath: string
  fileName: string
  resourceUrl: string
  previewUrl: string
}

export interface OfficialFontLibrary {
  defaultName: string
  items: OfficialFontItem[]
}

type RawOfficialFontMap = Record<string, unknown> & {
  defaultName?: unknown
}

let cachedLibrary: OfficialFontLibrary | null = null

function normalizePath(value: string): string {
  return String(value || '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/^\/+|\/+$/g, '')
    .replace(/\/+/g, '/')
}

function stripFileExtension(value: string): string {
  return normalizePath(value).split('/').filter(Boolean).pop()?.replace(/\.[^.]+$/u, '') ?? ''
}

function normalizeFontInfo(key: string, value: unknown): OfficialFontItem | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Partial<OfficialFontInfo>
  const name = String(raw.name || '').trim()
  const displayName = String(raw.displayName || name).trim()
  const path = normalizePath(String(raw.path || ''))
  const md5 = String(raw.md5 || '').trim().toLowerCase()
  const size = Math.floor(Number(raw.size || 0))
  const previewMd5 = String(raw.previewMd5 || '').trim().toLowerCase()
  if (!name || !path || !md5 || size <= 0) return null
  const keySegments = normalizePath(key).split('/').filter(Boolean)
  const fileName = path.split('/').filter(Boolean).pop() || keySegments[keySegments.length - 1] || name
  const categorySegments = keySegments.slice(0, -1)
  return {
    key: normalizePath(key),
    categoryPath: categorySegments.join('/'),
    fileName,
    name,
    displayName,
    path,
    md5,
    size,
    previewMd5,
    resourceUrl: buildCurrentProjectResourceUrl(md5),
    previewUrl: previewMd5 ? buildCurrentProjectResourceUrl(previewMd5) : '',
  }
}

function parseOfficialFontLibrary(raw: unknown): OfficialFontLibrary {
  if (!raw || typeof raw !== 'object') {
    throw new Error('官方字体映射文件格式错误')
  }
  const data = raw as RawOfficialFontMap
  const defaultName = String(data.defaultName || '').trim()
  const items: OfficialFontItem[] = []
  const usedNames = new Set<string>()
  for (const [key, value] of Object.entries(data)) {
    if (key === 'defaultName') continue
    const item = normalizeFontInfo(key, value)
    if (!item) continue
    if (usedNames.has(item.name)) {
      throw new Error(`官方字体映射存在重复字体名称：${item.name}`)
    }
    usedNames.add(item.name)
    items.push(item)
  }
  if (!defaultName) throw new Error('官方字体映射缺少 defaultName')
  if (!items.some((item) => item.name === defaultName)) {
    throw new Error(`官方默认字体不存在：${defaultName}`)
  }
  return { defaultName, items }
}

/** 读取内置官方字体映射；同一会话内缓存解析结果，避免重复整理分类。 */
export async function loadOfficialFontLibrary(force = false): Promise<OfficialFontLibrary> {
  if (!cachedLibrary || force) {
    cachedLibrary = parseOfficialFontLibrary(fontMap)
  }
  return cachedLibrary
}

/**
 * 按工程设置中的字体名称查找官方字体。
 * @param library 官方字体映射。
 * @param name 工程设置中的字体名称。
 * @returns 命中的官方字体；未命中返回 null。
 */
export function findOfficialFontByName(library: OfficialFontLibrary, name: string): OfficialFontItem | null {
  const target = String(name || '').trim()
  if (!target) return null
  const targetBase = stripFileExtension(target)
  return library.items.find((item) => {
    const candidates = [
      item.name,
      item.displayName,
      item.fileName,
      item.path,
      stripFileExtension(item.name),
      stripFileExtension(item.fileName),
      stripFileExtension(item.path),
    ]
    return candidates.some((candidate) => {
      const normalized = String(candidate || '').trim()
      return normalized === target || (!!targetBase && stripFileExtension(normalized) === targetBase)
    })
  }) ?? null
}

/**
 * 获取官方默认字体。
 * @param library 官方字体映射。
 * @returns defaultName 对应的字体。
 */
export function getDefaultOfficialFont(library: OfficialFontLibrary): OfficialFontItem {
  const item = findOfficialFontByName(library, library.defaultName)
  if (!item) {
    throw new Error(`官方默认字体不存在：${library.defaultName}`)
  }
  return item
}

/**
 * 按资源 MD5 查找官方字体。
 * @param library 官方字体映射。
 * @param md5 字体资源 MD5。
 * @returns 命中的官方字体；未命中返回 null。
 */
export function findOfficialFontByMd5(library: OfficialFontLibrary, md5: string): OfficialFontItem | null {
  const target = String(md5 || '').trim().toLowerCase()
  if (!target) return null
  return library.items.find((item) => item.md5 === target) ?? null
}

function resolveFontFolderSegments(item: OfficialFontItem): string[] {
  const pathSegments = normalizePath(item.path).split('/').filter(Boolean)
  const relativeSegments = pathSegments[0]?.toLowerCase() === 'font' ? pathSegments.slice(1, -1) : pathSegments.slice(0, -1)
  return [FONT_LIBRARY_DEFAULT_FOLDER, ...relativeSegments]
}

/** 把官方字体资源写入当前工程的字体虚拟资源树。 */
/**
 * 把官方字体资源写入当前工程字体虚拟资源树。
 * @param params.root 当前字体资源树。
 * @param params.item 官方字体条目。
 * @returns 新资源树、实际文件名和资源管理器目录。
 */
export function applyOfficialFontToResourceRoot(params: {
  root: DataFileMap | null
  item: OfficialFontItem
}): { root: DataFileMap; fileName: string; folderSegments: string[] } {
  const folderSegments = resolveFontFolderSegments(params.item)
  const mapped = appendMaterialResourceMappingEntry({
    root: params.root,
    category: 'font' satisfies ResourceCategoryKey,
    folderSegments,
    fileName: params.item.fileName,
    url: params.item.resourceUrl,
    sourceUrl: params.item.key,
    md5: params.item.md5,
    size: params.item.size,
    extraLinks: {
      [FONT_LIBRARY_KEY_LINK_INDEX]: params.item.key,
      [FONT_DISPLAY_NAME_LINK_INDEX]: params.item.displayName,
      [FONT_PREVIEW_MD5_LINK_INDEX]: params.item.previewMd5,
    },
    replaceSameName: true,
  })
  return { ...mapped, folderSegments }
}

/**
 * 根据官方字体 key 生成素材库字体分类树。
 * @param items 官方字体条目列表。
 * @returns Naive UI Tree 可直接使用的分类数据。
 */
export function collectOfficialFontCategoryTree(items: OfficialFontItem[]): Array<{
  key: string
  label: string
  children?: Array<{ key: string; label: string; children?: Array<{ key: string; label: string }> }>
}> {
  const roots = new Map<string, Map<string, OfficialFontItem[]>>()
  for (const item of items) {
    const segments = item.categoryPath.split('/').filter(Boolean)
    const top = segments[0] || '默认'
    const child = segments.slice(1).join('/') || ''
    if (!roots.has(top)) roots.set(top, new Map())
    const children = roots.get(top)!
    if (!children.has(child)) children.set(child, [])
    children.get(child)!.push(item)
  }
  return [...roots.entries()].map(([top, childMap]) => ({
    key: `category:${top}`,
    label: top,
    children: [...childMap.entries()].flatMap(([child, fonts]) => {
      const fontNodes = fonts.map((font) => ({
        key: `font:${font.key}`,
        label: font.displayName,
      }))
      if (!child) return fontNodes
      return [{
        key: `category:${top}/${child}`,
        label: child.split('/').pop() || child,
        children: fontNodes,
      }]
    }),
  }))
}
